/** @prettier */

//-------------------------------------
//		マイサイト操作画面
//-------------------------------------

import {useState, useEffect, memo, useContext} from 'react';
import {Grid, Box, Stack, IconButton, Tabs, Tab} from '@mui/material';
import {useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import ViewFloor from './ViewFloor';
import ViewGroup from './ViewGroup';
import ViewUnit from './ViewUnit';
import ViewOther from './ViewQtonHyozan';
//interface import
import * as apiIF from '../../Type/api';
//type import
import {
  GraphTargetType,
  GraphFormType,
  mysite,
  mysiteProps,
  layoutUnitListType,
  graphProps,
} from '../../Type/TypeDef';
//Style import
import '../../css/common/common.css';

import {iconWhite} from '../../define';
import {ContextApplyFilterTab} from './MySiteArea';
import type {ContextApplyFilterTabType} from './MySiteArea';
import {numberWithPeriod, roundOff} from '../../functions/convertString';
import {useGetUnitTypeList} from '../../hooks/composition/useGetUnitTypeList';

/**
 * サイト種別登録台数
 * @param siteData サイト情報
 * @returns 台数 [空調機台数, 熱源機台数, 冷凍機台数]
 */
const siteRegUnitsNumber = (siteData: mysite.siteData) => {
  /** エアコン登録台数 登録が無ければ0台 */
  const regAirconNum = Number(
    siteData.aircon === undefined ? '0' : siteData.aircon.registerNumber
  );
  /** 給湯機登録台数 登録が無ければ0台 */
  const regQtonNum = Number(
    siteData.qton === undefined ? '0' : siteData.qton.registerNumber
  );
  /** 冷凍機登録台数 登録が無ければ0台 */
  const regFreezerNum = Number(
    siteData.freezer === undefined ? '0' : siteData.freezer.registerNumber
  );

  return {regAirconNum, regQtonNum, regFreezerNum};
};

/**
 * サブコンテンツのコンテンツ部表示（再レンダリング抑止のためメモ化する）
 * @param props props
 * - viewType サブコンテンツの表示種別
 * - siteProfile サブコンテンツに表示しているサイトの情報
 * - floorFilter フロアのフィルタ
 * - groupFilter グループのフィルタ
 * - selectedItem サブコンテンツで選択している項目
 * - unitTypeList SL通信の室内機、またはインターフェースのID、型式情報一覧
 * - setSelectedItem サブコンテンツで選択している項目のset関数
 * - setUnitTypeList サイトに登録されている室内機、インターフェースのIDと型式一覧設定関数
 * - setGraphParam グラフに描画するためのデータ
 * - setOperationParam 指令ボタン押下時の実行関数
 * @returns JSX
 */
const StateContent = memo(
  ({
    viewType,
    siteProfile,
    floorFilter,
    groupFilter,
    otherFilter,
    selectedItem,
    unitTypeList,
    setSelectedItem,
    setGraphParam,
    setOperationParam,
  }: {
    viewType: mysite.tabProps['type'];
    siteProfile: apiIF.resParameters.siteInformations;
    floorFilter: string;
    groupFilter: string;
    otherFilter: mysite.filterProps['unitType'];
    selectedItem: string;
    unitTypeList: layoutUnitListType;
    setSelectedItem: React.Dispatch<React.SetStateAction<string>>;
    setGraphParam: graphProps['setGraphParam'];
    setOperationParam: (
      target: apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined,
      targetID: string,
      siteID: string
    ) => void;
  }) => {
    switch (viewType) {
      case 'floors':
        return (
          <ViewFloor
            siteProfile={siteProfile}
            setGraphParam={setGraphParam}
            setOperationParam={setOperationParam}
          />
        );
      case 'groups':
        return (
          <ViewGroup
            siteProfile={siteProfile}
            setGraphParam={setGraphParam}
            setOperationParam={setOperationParam}
            floorFilter={floorFilter}
          />
        );
      case 'units':
        return (
          <ViewUnit
            siteProfile={siteProfile}
            floorFilter={floorFilter}
            groupFilter={groupFilter}
            selectedItem={selectedItem}
            unitTypeList={unitTypeList}
            setSelectedItem={setSelectedItem}
            setGraphParam={setGraphParam}
            setOperationParam={setOperationParam}
          />
        );
      case 'other':
        return (
          <ViewOther
            siteProfile={siteProfile}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            setGraphParam={setGraphParam}
            otherFilter={otherFilter}
          />
        );
      default:
        return <></>;
    }
  }
);

/** サブコンテンツ タブ表示
 * @param props props
 * - siteData サイト情報
 * - viewType タブの種類
 * - applyFilter フィルタセット関数
 * - applyTab タブセット関数
 * @returns JSX
 */
const SelectTabView = (props: {
  siteData: mysite.siteData;
  viewType: mysite.tabProps['type'];
  applyFilter: (filter: ContextApplyFilterTabType['applyFilter']) => void;
  applyTab: (tab: ContextApplyFilterTabType['applyTab']) => void;
}) => {
  const trans = useTranslation().t; //翻訳関数
  /** サイトの空調機、熱源機、冷凍機の登録台数 */
  const {regAirconNum, regQtonNum, regFreezerNum} = siteRegUnitsNumber(
    props.siteData
  );

  //選択しているタブ
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    setSelectedTab(convertForMUI(props.viewType));
  }, [props.viewType]);

  useEffect(() => {
    switch (selectedTab) {
      case 0:
      default:
        props.applyTab('floors');
        break;
      case 1:
        props.applyTab('groups');
        break;
      case 2:
        props.applyTab('units');
        break;
      case 3:
        props.applyTab('other');
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  /**
   * MUI用にタブの値を変換
   * @param viewType タブ
   * @returns MUI用にタブの値
   */
  const convertForMUI = (viewType: mysite.tabProps['type']) => {
    switch (viewType) {
      case 'floors':
      default:
        return 0;
      case 'groups':
        return 1;
      case 'units':
        return 2;
      case 'other':
        return 3;
    }
  };

  /**
   * タブ選択変更時、フィルタを解除する
   * @param event
   * @param newValue
   */
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    props.applyFilter({floor: '', groupID: '', otherUnitType: ''});
  };

  return (
    <Box className="subContents__tabContainer">
      <Tabs
        value={selectedTab}
        onChange={handleChange}
        textColor="inherit"
        className="subContents__tabContainer__tabs">
        <Tab
          label={
            <Box className="subContents__tabContainer__tabs__tab__text">
              {trans('SiteSubContents.tab.floor')}
            </Box>
          }
          disabled={regAirconNum <= 0}
          className="subContents__tabContainer__tabs__tab"
        />
        <Tab
          label={
            <Box className="subContents__tabContainer__tabs__tab__text">
              {trans('SiteSubContents.tab.group')}
            </Box>
          }
          disabled={regAirconNum <= 0}
          className="subContents__tabContainer__tabs__tab"
        />
        <Tab
          label={
            <Box className="subContents__tabContainer__tabs__tab__text">
              {trans('SiteSubContents.tab.unit')}
            </Box>
          }
          disabled={regAirconNum <= 0}
          className="subContents__tabContainer__tabs__tab"
        />
        <Tab
          label={
            <Box className="subContents__tabContainer__tabs__tab__text">
              {trans('SiteSubContents.tab.other')}
            </Box>
          }
          disabled={regQtonNum + regFreezerNum <= 0}
          className="subContents__tabContainer__tabs__tab"
        />
      </Tabs>
    </Box>
  );
};

/**
 * 機種別のサブコンテンツヘッダ部のアイコンと台数表示
 * @param props props
 * - siteData サイト台数データ
 * - iconData アイコンデータ
 * @returns JSX
 */
const ViewUnitStatusHeader = (props: {
  siteData: mysiteProps.unitStatusNum;
  iconData: string;
}) => {
  const trans = useTranslation().t; //翻訳関数

  return (
    <Grid container columnSpacing={0.5}>
      {/** ユニットアイコン、運転台数 */}
      <Grid item xs="auto">
        <Stack spacing={1} alignItems="center">
          {/** ユニットアイコン */}
          <Box className="subContents__basicData__unitIcon__border">
            <img
              src={props.iconData}
              alt={`${trans('SiteSubContents.image.alternativeText.unitIcon')}`}
              className="subContents__basicData__unitIcon__icon"
            />
          </Box>
          {/** 運転台数 */}
          <Box className="subContents__basicData__runNum">
            <Box
              component="span"
              className="subContents__basicData__runNum--run">
              {props.siteData.operatingNumber}
            </Box>
            &nbsp;/&nbsp;{props.siteData.registerNumber}
          </Box>
        </Stack>
      </Grid>
      {/** 異常アイコン */}
      {/** 異常時のみ表示する */}
      {props.siteData.malfunctioningNumber === '0' ? (
        <></>
      ) : (
        <Grid item xs="auto">
          <Stack alignItems="center" spacing={0.5}>
            <Box className="subContents__basicData__errorIcon__border">
              <img
                src={iconWhite.ERROR.src}
                alt={`${trans(
                  `SiteSubContents.image.alternativeText.${iconWhite.ERROR.alt}`
                )}`}
                className="subContents__basicData__errorIcon__icon"
              />
            </Box>
            <Box
              component="span"
              className="subContents__basicData__errorIcon__text">
              {props.siteData.malfunctioningNumber}
            </Box>
          </Stack>
        </Grid>
      )}
    </Grid>
  );
};

/**
 * サブコンテンツ表示
 * @param props props
 * - siteProfile サブコンテンツに表示しているサイトの情報
 * - setSiteProfile サブコンテンツに表示しているサイトの情報のset関数
 * - siteNumInfoList 画面に表示するサイトの機器毎の稼働台数
 * - sitePowerInfoList 画面に表示するサイトの機器毎の消費電力
 * - selectedItem サブコンテンツで選択している項目
 * - setSelectedItem サブコンテンツで選択している項目のset関数
 * - setUnitTypeList SL通信の室内機とインターフェースのID、型式一覧の設定関数
 * - operationOpen 指令ボタン押下時の実行関数
 * - setGraphParam グラフに描画するためのデータ
 * - viewType サブコンテンツの表示種別
 * - floorFilter フロアフィルター
 * - groupFilter グループフィルター
 * - otherFilter その他フィルター
 * @returns サブコンテンツ
 */
const SiteSubContents = memo(
  (props: {
    siteProfile: apiIF.resParameters.siteInformations;
    setSiteProfile: React.Dispatch<
      React.SetStateAction<apiIF.resParameters.siteInformations | undefined>
    >;
    siteNumInfoList: mysite.siteData[];
    sitePowerInfoList: mysiteProps.siteKWData[];
    selectedItem: string;
    setSelectedItem: React.Dispatch<React.SetStateAction<string>>;
    setUnitTypeList: React.Dispatch<React.SetStateAction<layoutUnitListType>>;
    operationOpen: (
      target: apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined,
      targetID: string,
      siteID: string
    ) => void;
    setGraphParam: graphProps['setGraphParam'];
    viewType: mysite.tabProps['type'];
    floorFilter: string;
    groupFilter: string;
    otherFilter: mysite.filterProps['unitType'];
  }) => {
    const trans = useTranslation().t; //翻訳関数
    //URL取得
    const location = useLocation();
    /** フィルタ、タブ状態をセットする関数 */
    const {applyFilter, applyTab} = useContext(ContextApplyFilterTab);
    //prettier-ignore
    //SL通信の室内機とインターフェースの登録情報を画面読み込み時に取得するフック
    const {unitTypeList} = useGetUnitTypeList(props.siteProfile['site-id']);

    //prettier-ignore
    //サイトの機器毎の稼働台数
    const [viewSiteData, setViewSiteData] = useState<mysite.siteData>({'siteID': props.siteProfile['site-id']});
    //prettier-ignore
    //サイトの機器毎の消費電力
    const [viewSitePwData, setViewSitePwData] = useState<mysiteProps.siteKWData>({'site-id': props.siteProfile['site-id']});

    //フィルター解除、タブ初期化
    useEffect(() => {
      /** サイトID */
      const siteId = props.siteProfile['site-id'];
      /**
       * タブの初期値
       * @param siteData サイト情報データ
       * @returns MUI用にタブの値
       */
      const defaultTab = (
        siteData: mysite.siteData
      ): mysite.tabProps['type'] => {
        /** サイトの空調機、熱源機、冷凍機の登録台数 */
        const {regAirconNum, regQtonNum, regFreezerNum} =
          siteRegUnitsNumber(siteData);

        //熱源機、冷凍機のみ
        if (regAirconNum <= 0 && regQtonNum + regFreezerNum > 0) {
          return 'other';
        }

        return 'floors';
      };
      /** サイトの機器毎の稼働台数 */
      const siteNumInfo = props.siteNumInfoList.find(info => {
        return info['siteID'] === siteId;
      });

      //タブ初期化
      if (siteNumInfo === undefined) {
        applyTab(defaultTab({siteID: siteId}));
      } else {
        applyTab(defaultTab(siteNumInfo));
      }
      //フィルター解除
      applyFilter({floor: '', groupID: '', otherUnitType: ''});
    }, [props.siteProfile]);

    //GroupLayOut画面表示用にSL通信の室内機、インターフェースの型式リストを送る
    useEffect(() => {
      if (unitTypeList.length > 0) {
        props.setUnitTypeList(unitTypeList);
      }
    }, [unitTypeList]);

    //サイトの機器毎の稼働台数取得用
    //サイトの機器毎の消費電力取得用
    useEffect(() => {
      /** サイトID */
      const siteId = props.siteProfile['site-id'];

      /** サイトの機器毎の稼働台数 */
      const siteNumInfo = props.siteNumInfoList.find(info => {
        return info['siteID'] === siteId;
      });
      if (siteNumInfo === undefined) {
        setViewSiteData({siteID: siteId});
      } else {
        setViewSiteData(siteNumInfo);
      }

      /** サイトの機器毎の消費電力 */
      const sitePowerInfo = props.sitePowerInfoList.find(info => {
        return info['site-id'] === siteId;
      });
      if (sitePowerInfo === undefined) {
        setViewSitePwData({'site-id': siteId});
      } else {
        setViewSitePwData(sitePowerInfo);
      }
    }, [props.siteProfile, props.siteNumInfoList, props.sitePowerInfoList]);

    /**
     * グラフボタン押下時の処理
     * @param target? {'floors' | 'groups' | 'sl' | 'site'} グラフに描画する対象
     * @param targetID? {string} グラフに描画する対象のID
     * @param type {'bar' | 'line' | ''} グラフコンポーネントの種類を選択 非表示にする場合は''
     * @param title? {string} グラフコンポーネントに表示するタイトル(csvのファイル名でも使用)
     * @returns
     */
    const graphSelect = (
      target: GraphTargetType,
      targetID?: string,
      type?: GraphFormType,
      title?: string
    ) => {
      props.setGraphParam(target, targetID, type, title);
    };

    return (
      <Stack className="subContents">
        {/** サイトの名前、グラフ、操作 */}
        <Grid
          container
          /** マイサイトを表示している時のみ赤背景 */
          className={
            location.pathname.endsWith('my-site')
              ? 'subContents__title--selected'
              : 'subContents__title'
          }
          justifyContent="space-between"
          alignItems="center">
          {/** サイトの名前 */}
          <Grid
            item
            xs
            title={props.siteProfile['site-name']}
            className="subContents__title__siteName">
            {props.siteProfile['site-name']}
          </Grid>
          {/** グラフ、操作 */}
          <Grid item xs="auto">
            <IconButton
              id="mv-subConHeaderBarChartBtn"
              onClick={() => {
                graphSelect(
                  'site',
                  props.siteProfile['site-id'],
                  'bar',
                  props.siteProfile['site-name']
                );
              }}>
              <img
                className="subContents__title__icon"
                title={`${trans(
                  `SiteSubContents.image.title.${iconWhite.BAR_GRAPH.title}`
                )}`}
                src={iconWhite.BAR_GRAPH.src}
                alt={`${trans(
                  `SiteSubContents.image.alternativeText.${iconWhite.BAR_GRAPH.alt}`
                )}`}
              />
            </IconButton>
            {/** manager以上かつ空調機がある場合のみ表示 */}
            {(props.siteProfile['site-authority'] === 'admin' ||
              props.siteProfile['site-authority'] === 'manager') &&
            viewSiteData.aircon !== undefined &&
            Number(viewSiteData.aircon.registerNumber) > 0 ? (
              <IconButton
                id="mv-subConHeaderOperationBtn"
                onClick={() =>
                  props.operationOpen('site', '', props.siteProfile['site-id'])
                }>
                <img
                  className="subContents__title__icon"
                  title={`${trans(
                    `SiteSubContents.image.title.${iconWhite.CONTROLLER.title}`
                  )}`}
                  src={iconWhite.CONTROLLER.src}
                  alt={`${trans(
                    `SiteSubContents.image.alternativeText.${iconWhite.CONTROLLER.alt}`
                  )}`}
                />
              </IconButton>
            ) : (
              <></>
            )}
            {/** マイサイトを表示している時のみ表示 */}
            {location.pathname.endsWith('my-site') ? (
              <IconButton
                id="mv-subConCloseBtn"
                onClick={() => props.setSiteProfile(undefined)}>
                <img
                  className="subContents__title__icon"
                  src={iconWhite.CLOSE.src}
                  alt={`${trans(
                    `SiteSubContents.image.alternativeText.${iconWhite.CLOSE.alt}`
                  )}`}
                />
              </IconButton>
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
        {/** 運転台数、消費電力量 */}
        <Grid container alignItems="center" className="subContents__basicData">
          {/** 運転台数 */}
          {/* 空調機 */}
          {viewSiteData.aircon !== undefined ? (
            <Grid
              item
              xs={2.5}
              className={`${
                viewSiteData.aircon.registerNumber === '0'
                  ? 'subContents__basicData--unregistered'
                  : ''
              }`}>
              <ViewUnitStatusHeader
                siteData={viewSiteData.aircon}
                iconData={iconWhite.AC_ON.src}
              />
            </Grid>
          ) : (
            <></>
          )}
          {/* 冷凍機 */}
          {viewSiteData.freezer !== undefined ? (
            <Grid
              item
              xs={2.5}
              className={`${
                viewSiteData.freezer.registerNumber === '0'
                  ? 'subContents__basicData--unregistered'
                  : ''
              }`}>
              <ViewUnitStatusHeader
                siteData={viewSiteData.freezer}
                iconData={iconWhite.FREEZER_ON.src}
              />
            </Grid>
          ) : (
            <></>
          )}
          {/* 給湯機 */}
          {viewSiteData.qton !== undefined ? (
            <Grid
              item
              xs={2.5}
              className={`${
                viewSiteData.qton.registerNumber === '0'
                  ? 'subContents__basicData--unregistered'
                  : ''
              }`}>
              <ViewUnitStatusHeader
                siteData={viewSiteData.qton}
                iconData={iconWhite.HOT_WATER_ON.src}
              />
            </Grid>
          ) : (
            <></>
          )}
          {/** 消費電力量 */}
          <Grid item xs className="subContents__basicData__power">
            <Box
              component="span"
              className="subContents__basicData__power__number">
              {viewSitePwData['site'] &&
                numberWithPeriod(roundOff(viewSitePwData['site']))}
            </Box>
            <Box
              component="span"
              className="subContents__basicData__power__kwh">
              {trans('SiteSubContents.data.unit')}
            </Box>
          </Grid>
        </Grid>
        {/** タブ */}
        <SelectTabView
          siteData={viewSiteData}
          viewType={props.viewType}
          applyFilter={applyFilter}
          applyTab={applyTab}
        />
        {/** 一覧 */}
        <Stack className="subContents__content">
          <ContextApplyFilterTab.Provider value={{applyFilter, applyTab}}>
            {/** サイトに機器が1台以上登録されているか確認 */}
            {(viewSiteData.aircon !== undefined &&
              Number(viewSiteData.aircon.registerNumber) > 0) ||
            (viewSiteData.freezer !== undefined &&
              Number(viewSiteData.freezer.registerNumber) > 0) ||
            (viewSiteData.qton !== undefined &&
              Number(viewSiteData.qton.registerNumber) > 0) ? (
              //機器が登録されている場合、状態値を表示
              <StateContent
                viewType={props.viewType}
                siteProfile={props.siteProfile}
                floorFilter={props.floorFilter}
                groupFilter={props.groupFilter}
                otherFilter={props.otherFilter}
                selectedItem={props.selectedItem}
                unitTypeList={unitTypeList}
                setSelectedItem={props.setSelectedItem}
                setGraphParam={props.setGraphParam}
                setOperationParam={props.operationOpen}
              />
            ) : (
              //機器が1台も登録されていない場合、メッセージを表示
              <Box className="subContents__content__noRegisterMessage">
                {trans('SiteSubContents.noRegisterMessage')}
              </Box>
            )}
          </ContextApplyFilterTab.Provider>
        </Stack>
      </Stack>
    );
  }
);

export default SiteSubContents;
